import React, { useRef, useState } from "react";

export default function SplitCode({
    value,                    // optional "########" (8 digits)
    onChange,                 // optional cb(newValue) with 8 digits (or "")
    groupSize = 4,
}) {
    const [part1, setPart1] = useState(value ? value.slice(0, groupSize) : "");
    const [part2, setPart2] = useState(value ? value.slice(groupSize, groupSize * 2) : "");
    const r1 = useRef(null);
    const r2 = useRef(null);

    const emit = (p1, p2) => {
        if (onChange) onChange(`${p1}${p2}`);
    };

    const clean = (s) => s.replace(/\D/g, "").slice(0, groupSize);

    const handleInput = (which) => (e) => {
        const raw = clean(e.target.value);
        if (which === 1) {
            setPart1(raw);
            // auto-advance when full
            if (raw.length === groupSize) {
                r2.current?.focus();
                // keep keyboard consistent; don't change type, just move focus
            }
            emit(raw, part2);
        } else {
            setPart2(raw);
            emit(part1, raw);
        }
    };

    // Backspace borrow: if field 2 is empty and user hits Backspace, pull from field 1
    const handleKeyDown = (which) => (e) => {
        if (e.key !== "Backspace") return;

        if (which === 2 && part2.length === 0) {
            if (part1.length > 0) {
                const p1 = part1.slice(0, -1);
                setPart1(p1);
                emit(p1, part2);
            }
            r1.current?.focus();
            // prevent browser from navigating back on mobile long-press, etc.
            e.preventDefault();
        }
    };

    // Smart paste: allow pasting full 8 digits (or 4) into either field
    const handlePaste = (which) => (e) => {
        const text = (e.clipboardData || window.clipboardData).getData("text");
        const digits = text.replace(/\D/g, "").slice(0, groupSize * 2);
        if (!digits) return;

        e.preventDefault();

        if (digits.length <= groupSize) {
            // just fill the current input
            if (which === 1) {
                setPart1(digits);
                emit(digits, part2);
                if (digits.length === groupSize) r2.current?.focus();
            } else {
                setPart2(digits);
                emit(part1, digits);
            }
        } else {
            const p1 = digits.slice(0, groupSize);
            const p2 = digits.slice(groupSize, groupSize * 2);
            setPart1(p1);
            setPart2(p2);
            emit(p1, p2);
            r2.current?.focus();
        }
    };

    // Keep both fields identical in attributes so the on-screen keyboard doesn't change
    const commonProps = {
        type: "text",
        inputMode: "numeric",         // numeric keypad on mobile without switching types
        pattern: "\\d*",              // hint for some keyboards
        autoComplete: "one-time-code",
        maxLength: groupSize,
        className: "code-chunk",
        placeholder: "•".repeat(groupSize),
        // Optional: prevent non-digits on keypress (not strictly needed since we sanitize)
        onBeforeInput: (e) => {
            if (e.data && /\D/.test(e.data)) e.preventDefault();
        },
    };

    return (
        <div
            className="input-split-code"
            style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontFeatureSettings: '"tnum"',
            }}
        >
            <input
                {...commonProps}
                ref={r1}
                value={part1}
                onChange={handleInput(1)}
                onPaste={handlePaste(1)}
            />
            <span aria-hidden="true">-</span>
            <input
                {...commonProps}
                ref={r2}
                value={part2}
                onChange={handleInput(2)}
                onKeyDown={handleKeyDown(2)}
                onPaste={handlePaste(2)}
            />
        </div>
    );
}